/** Debug: colored horizontal markers at wrapped text line bottoms (preview only). */

const BULLET_TEXT_LINE_SELECTORS = 'li > span.flex-1, li > .paysafe-preview-bullet-text'

const RIGHT_COLUMN_LINE_SELECTORS =
  '.paysafe-right-block > div, .paysafe-right-block > a, .paysafe-right-block > h3'

/** Mirrors break-snap column line carriers — avoids duplicate nested rects. */
const COLUMN_LINE_CARRIER_SELECTORS = [
  'h2',
  'h3',
  'p',
  BULLET_TEXT_LINE_SELECTORS,
  '.paysafe-skill-block h3',
  '.paysafe-skill-block .paysafe-skill-body',
  '.paysafe-skill-block .paysafe-skill-token',
  '.paysafe-skill-block .text-sm',
  RIGHT_COLUMN_LINE_SELECTORS,
  '[data-role-id] p',
].join(',')

const HEADER_SCOPE_SELECTOR = '[data-paysafe-header]'
const HEADER_END_MARKER_SELECTOR = '[data-line-guide-header-end]'

/** Only these nodes inside `[data-paysafe-header]` — not border rules or column body. */
const HEADER_LINE_CARRIER_SELECTORS = [
  `${HEADER_SCOPE_SELECTOR} h1`,
  `${HEADER_SCOPE_SELECTOR} .contact-detail-line`,
].join(',')

/** Merge stripes closer than this — overlapping duplicates read as one thick line. */
export const RESUME_LINE_GUIDE_Y_TOLERANCE_PX = 2

/** Stronger collapse for header carriers (Range fragments per visual line). */
export const RESUME_HEADER_LINE_GUIDE_Y_TOLERANCE_PX = 4

export type ResumeLineGuideBlock =
  | 'header'
  | 'header-end'
  | 'target'
  | 'summary'
  | 'section'
  | 'role'
  | 'bullet'
  | 'skills'
  | 'right'
  | 'other'

export type ResumeLineGuideKind = 'text-line' | 'header-end' | 'page-edge'

export type ResumeLineGuide = {
  y: number
  block: ResumeLineGuideBlock
  color: string
  kind?: ResumeLineGuideKind
}

export type ResumeLineGuideStripeStyle = {
  top: number
  height: number
  opacity: number
  backgroundColor?: string
  backgroundImage?: string
  boxShadow?: string
  label?: string
}

/** Map collected guide to overlay stripe — landmarks use thicker / dashed styles. */
export function stripeStyleForResumeLineGuide(g: ResumeLineGuide): ResumeLineGuideStripeStyle {
  const kind = g.kind ?? (g.block === 'header-end' ? 'header-end' : 'text-line')
  if (kind === 'header-end' || g.block === 'header-end') {
    return {
      top: g.y,
      height: 3,
      opacity: 1,
      backgroundColor: g.color,
      backgroundImage:
        'repeating-linear-gradient(90deg, transparent 0 6px, rgba(255,255,255,0.55) 6px 12px)',
      boxShadow: '0 0 0 1px rgba(2,132,199,0.35)',
      label: 'низ шапки',
    }
  }
  if (g.block === 'right') {
    return {
      top: g.y,
      height: 2,
      opacity: 0.95,
      backgroundColor: g.color,
      boxShadow: `inset 4px 0 0 0 ${g.color}`,
    }
  }
  if (g.block === 'header') {
    return { top: g.y, height: 1, opacity: 0.55, backgroundColor: g.color }
  }
  return { top: g.y, height: 1, opacity: 0.8, backgroundColor: g.color }
}

export const RESUME_LINE_GUIDE_PAGE_BREAK_COLOR = '#e11d48'

/** Distinct from page clip edge — full-width dashed band under header. */
export const RESUME_LINE_GUIDE_HEADER_END_COLOR = '#0284c7'

export const RESUME_LINE_GUIDE_COLORS: Record<ResumeLineGuideBlock, string> = {
  header: '#94a3b8',
  'header-end': RESUME_LINE_GUIDE_HEADER_END_COLOR,
  target: '#14b8a6',
  summary: '#3b82f6',
  section: '#f97316',
  role: '#a855f7',
  bullet: '#22c55e',
  skills: '#eab308',
  right: '#ec4899',
  other: '#64748b',
}

export function classifyResumeLineGuideBlock(el: HTMLElement): ResumeLineGuideBlock {
  if (el.closest('[data-paysafe-header]')) return 'header'
  if (el.closest('.paysafe-preview-bullet-text') || el.classList.contains('paysafe-preview-bullet-text')) {
    return 'bullet'
  }
  if (el.closest('[data-role-id]')) return 'role'
  if (el.matches('h2.resume-section-header') || el.closest('h2.resume-section-header')) return 'section'
  if (
    el.matches('h2.text-base') ||
    el.closest('h2.text-base') ||
    (el.tagName === 'H2' && el.classList.contains('font-semibold'))
  ) {
    return 'target'
  }
  if (el.closest('.paysafe-skill-block')) return 'skills'
  if (el.closest('.paysafe-right-block')) return 'right'
  if (el.matches('h3') || el.closest('h3')) return 'role'
  if (el.matches('p') || el.closest('p')) return 'summary'
  return 'other'
}

function isBulletTextSpan(el: HTMLElement): boolean {
  const parent = el.parentElement
  return (
    parent?.tagName === 'LI' &&
    (el.classList.contains('flex-1') || el.classList.contains('paysafe-preview-bullet-text'))
  )
}

function isBulletMarkerSpan(el: HTMLElement): boolean {
  const parent = el.parentElement
  return parent?.tagName === 'LI' && el.tagName === 'SPAN' && !isBulletTextSpan(el)
}

function shouldSkipLineGuideCarrier(el: HTMLElement): boolean {
  if (isBulletMarkerSpan(el)) return true
  if (el.tagName === 'LI' && el.querySelector(BULLET_TEXT_LINE_SELECTORS)) return true
  if (el.classList.contains('border-b') || el.classList.contains('border-t')) return true
  const text = el.textContent?.trim()
  if (!text) return true
  const style = window.getComputedStyle(el)
  if (style.display === 'none' || style.visibility === 'hidden') return true
  if (el.closest('.paysafe-right-block') && !el.matches(RIGHT_COLUMN_LINE_SELECTORS)) {
    return true
  }
  if (el.closest('[data-role-id]') && el.tagName !== 'P' && !isBulletTextSpan(el)) {
    return true
  }
  if (el.closest('.paysafe-skill-block')) {
    return false
  }
  const parent = el.parentElement
  if (parent?.tagName === 'LI' && el.tagName === 'SPAN' && !isBulletTextSpan(el)) return true
  return false
}

type LineGuideCandidate = ResumeLineGuide & { width: number }

export function lineGuideYBucket(y: number, tolerance = RESUME_LINE_GUIDE_Y_TOLERANCE_PX): number {
  return Math.round(y / tolerance) * tolerance
}

function lineHeightPx(el: HTMLElement): number {
  const style = window.getComputedStyle(el)
  const fontSize = parseFloat(style.fontSize) || 14
  if (style.lineHeight === 'normal') return fontSize * 1.2
  const lh = parseFloat(style.lineHeight)
  return Number.isFinite(lh) && lh > 0 ? lh : fontSize * 1.2
}

/** Collapse Range fragment rects that belong to the same visual text line. */
export function collapseCarrierLineBottoms(
  lines: Array<{ bottom: number; width: number }>,
  lineH: number,
  tolerance = RESUME_LINE_GUIDE_Y_TOLERANCE_PX
): Array<{ bottom: number; width: number }> {
  if (lines.length <= 1) return lines
  const mergeTolerance = Math.max(tolerance, Math.round(lineH * 0.3))
  const byBucket = new Map<number, { bottom: number; width: number }>()
  for (const line of lines) {
    const bucket = lineGuideYBucket(line.bottom, mergeTolerance)
    const prev = byBucket.get(bucket)
    if (!prev || line.bottom > prev.bottom || (line.bottom === prev.bottom && line.width > prev.width)) {
      byBucket.set(bucket, line)
    }
  }
  return Array.from(byBucket.values()).sort((a, b) => a.bottom - b.bottom)
}

/** Per wrapped line: bottom edge in coordinateRoot local px (overlay space). */
export function lineBottomsForGuideCarrier(
  el: HTMLElement,
  coordinateRoot: HTMLElement,
  coordinateScale = 1
): Array<{ bottom: number; width: number }> {
  const rootTop = coordinateRoot.getBoundingClientRect().top
  const lineH = lineHeightPx(el)
  const maxLineH = lineH * 1.4
  const range = document.createRange()
  range.selectNodeContents(el)
  const rects = range.getClientRects()
  const out: Array<{ bottom: number; width: number }> = []
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i]
    if (!r || r.width < 1 || r.height < 0.5) continue
    if (r.height > maxLineH) continue
    out.push({
      bottom: visualOffsetYToLocalGuideY(r.bottom - rootTop, coordinateScale),
      width: r.width,
    })
  }
  return collapseCarrierLineBottoms(out, lineH)
}

/** Collapse nearby Y values; keep the lowest stripe (max bottom) per bucket. */
export function dedupeResumeLineGuides(
  candidates: LineGuideCandidate[],
  tolerance = RESUME_LINE_GUIDE_Y_TOLERANCE_PX
): ResumeLineGuide[] {
  const byBucket = new Map<number, LineGuideCandidate>()
  for (const c of candidates) {
    const bucket = lineGuideYBucket(c.y, tolerance)
    const prev = byBucket.get(bucket)
    if (!prev || c.y > prev.y || (c.y === prev.y && c.width > prev.width)) {
      byBucket.set(bucket, c)
    }
  }
  return Array.from(byBucket.values())
    .map(({ width: _w, ...guide }) => guide)
    .sort((a, b) => a.y - b.y)
}

function rectsForLineGuide(
  el: HTMLElement,
  coordinateRoot: HTMLElement,
  coordinateScale = 1
): LineGuideCandidate[] {
  const block = classifyResumeLineGuideBlock(el)
  const color = RESUME_LINE_GUIDE_COLORS[block]
  const lines = lineBottomsForGuideCarrier(el, coordinateRoot, coordinateScale)
  return lines.map(({ bottom, width }) => ({
    y: bottom,
    block,
    color,
    width,
  }))
}

function collectSkillBlockLineGuides(
  block: HTMLElement,
  coordinateRoot: HTMLElement,
  coordinateScale = 1
): LineGuideCandidate[] {
  const out: LineGuideCandidate[] = []
  const title = block.querySelector('h3')
  if (title instanceof HTMLElement && !shouldSkipLineGuideCarrier(title)) {
    out.push(...rectsForLineGuide(title, coordinateRoot, coordinateScale))
  }
  const tokens = block.querySelectorAll('.paysafe-skill-token')
  if (tokens.length > 0) {
    tokens.forEach((token) => {
      if (token instanceof HTMLElement && !shouldSkipLineGuideCarrier(token)) {
        out.push(...rectsForLineGuide(token, coordinateRoot, coordinateScale))
      }
    })
    return out
  }
  const body = block.querySelector('.paysafe-skill-body, .text-sm')
  if (body instanceof HTMLElement && !shouldSkipLineGuideCarrier(body)) {
    out.push(...rectsForLineGuide(body, coordinateRoot, coordinateScale))
  }
  return out
}

function headerScopeBottomY(
  headerRoot: HTMLElement,
  coordinateRoot: HTMLElement,
  coordinateScale = 1
): number {
  const marker = headerRoot.querySelector(HEADER_END_MARKER_SELECTOR)
  const anchor = marker instanceof HTMLElement ? marker : headerRoot
  return visualOffsetYToLocalGuideY(
    anchor.getBoundingClientRect().bottom - coordinateRoot.getBoundingClientRect().top,
    coordinateScale
  )
}

function collectHeaderScopeLineGuides(
  scopeRoot: HTMLElement,
  coordinateRoot: HTMLElement,
  coordinateScale = 1
): ResumeLineGuide[] {
  const candidates: LineGuideCandidate[] = []
  const carriers = new Set<HTMLElement>()

  scopeRoot.querySelectorAll(HEADER_LINE_CARRIER_SELECTORS).forEach((node) => {
    if (node instanceof HTMLElement) carriers.add(node)
  })

  carriers.forEach((node) => {
    if (shouldSkipLineGuideCarrier(node)) return
    candidates.push(...rectsForLineGuide(node, coordinateRoot, coordinateScale))
  })

  const headerBottomY = headerScopeBottomY(scopeRoot, coordinateRoot, coordinateScale)
  const textGuides = dedupeResumeLineGuides(candidates, RESUME_HEADER_LINE_GUIDE_Y_TOLERANCE_PX).filter(
    (g) => g.y <= headerBottomY + 0.5
  )

  return [
    ...textGuides,
    {
      y: headerBottomY,
      block: 'header-end',
      color: RESUME_LINE_GUIDE_COLORS['header-end'],
      kind: 'header-end',
    },
  ]
}

export type CollectResumePreviewLineGuidesOptions = {
  /** Only collect inside this selector (e.g. header band on sheet 1). */
  scopeSelector?: string
  /**
   * When preview is scaled via CSS transform (splitter resize), getBoundingClientRect
   * returns viewport px but overlay `top` uses unscaled layout px — divide by this.
   */
  coordinateScale?: number
}

/** Map viewport Y delta (inside a scaled preview) to local overlay coordinates. */
export function visualOffsetYToLocalGuideY(
  visualOffsetPx: number,
  coordinateScale = 1
): number {
  if (!Number.isFinite(visualOffsetPx)) return 0
  if (coordinateScale > 0 && coordinateScale !== 1) {
    return visualOffsetPx / coordinateScale
  }
  return visualOffsetPx
}

export function collectResumePreviewLineGuides(
  root: HTMLElement,
  options: CollectResumePreviewLineGuidesOptions = {}
): ResumeLineGuide[] {
  const coordinateScale = options.coordinateScale ?? 1
  const scopeRoot =
    options.scopeSelector != null
      ? root.querySelector<HTMLElement>(options.scopeSelector) ?? null
      : root
  if (!scopeRoot) return []

  /** Overlay is always positioned in `root` — measure Y in the same coordinate space. */
  const coordinateRoot = root
  const candidates: LineGuideCandidate[] = []
  const carriers = new Set<HTMLElement>()

  if (options.scopeSelector === HEADER_SCOPE_SELECTOR) {
    return collectHeaderScopeLineGuides(scopeRoot, coordinateRoot, coordinateScale)
  }

  scopeRoot.querySelectorAll('.paysafe-skill-block').forEach((block) => {
    if (block instanceof HTMLElement) {
      candidates.push(...collectSkillBlockLineGuides(block, coordinateRoot, coordinateScale))
    }
  })

  scopeRoot.querySelectorAll(COLUMN_LINE_CARRIER_SELECTORS).forEach((node) => {
    if (!(node instanceof HTMLElement)) return
    if (node.closest('.paysafe-skill-block')) return
    carriers.add(node)
  })

  carriers.forEach((node) => {
    if (shouldSkipLineGuideCarrier(node)) return
    candidates.push(...rectsForLineGuide(node, coordinateRoot, coordinateScale))
  })

  return dedupeResumeLineGuides(candidates)
}
